import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Send,
  Ticket,
  Trash2,
} from "lucide-react";
import {
  isContainmentOverdue,
  toLocalIsoDate,
  type ContainmentActionStatus,
  type ContainmentEffectiveness,
  type ContainmentPlanAction,
  type ContainmentPlanParams,
  type ContainmentPriority,
} from "@playbookai/widget-schemas";
import { DisabledReason } from "../../molecules/index.js";
import { cn } from "../../lib/utils.js";
import { WidgetPersonField } from "../person-field.js";
import type { WidgetBodyProps, WidgetChangeOptions } from "../types.js";
import { usePeopleSource } from "../user-directory.js";
import { ContainmentActionDetailsFields } from "./ActionDetailsFields.js";
import { ContainmentChip } from "./ContainmentChip.js";
import {
  EFFECTIVENESS_RANK,
  EFFECTIVENESS_SHORT,
  EFFECT_TONE,
  PRIORITY_RANK,
  STATUS_RANK,
  STATUS_TONE,
  localDateMs,
  noteTime,
} from "./presentation.js";

/**
 * The pre-`ExpandedComponent` editor, still reachable through the legacy
 * `ExpandedWidgetEditor` switch and the package barrel. The canvas renders
 * `ContainmentPlanExpandedEditor` (the `def.ExpandedComponent` seam) instead.
 */

const PRIORITIES: ContainmentPriority[] = ["High", "Medium", "Low"];
const STATUSES: ContainmentActionStatus[] = [
  "Open",
  "In progress",
  "Done",
  "Stopped",
];
const EFFECTIVENESS: ContainmentEffectiveness[] = [
  "Not yet assessed",
  "Effective",
  "Partially effective",
  "Not effective",
];

let fallbackIdSequence = 0;

function nextEditorId(prefix: string): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  fallbackIdSequence += 1;
  return `${prefix}-${Date.now().toString(36)}-${fallbackIdSequence.toString(36)}`;
}

function RollupStrip({
  actions,
}: {
  actions: ContainmentPlanAction[];
}): ReactElement {
  const byStatus = Object.fromEntries(
    STATUSES.map((status) => [
      status,
      actions.filter((action) => action.status === status).length,
    ]),
  ) as Record<ContainmentActionStatus, number>;
  const byEffectiveness = Object.fromEntries(
    EFFECTIVENESS.map((effectiveness) => [
      effectiveness,
      actions.filter((action) => action.effectiveness === effectiveness).length,
    ]),
  ) as Record<ContainmentEffectiveness, number>;
  const overdueCount = actions.filter((action) =>
    isContainmentOverdue(action),
  ).length;
  const unowned = actions.filter((action) => !action.owner.trim()).length;

  return (
    <div className="flex shrink-0 flex-wrap items-stretch gap-2">
      <Metric label="Total" value={actions.length} />
      <div className="flex items-center gap-1.5 rounded-md border border-pb-border bg-pb-card-elevated/40 px-2.5 py-1.5">
        <span className="text-dense-2 uppercase tracking-wider text-pb-text-muted">
          Status
        </span>
        {STATUSES.map((status) => (
          <ContainmentChip key={status} className={STATUS_TONE[status]}>
            {status === "In progress" ? "In prog" : status} {byStatus[status]}
          </ContainmentChip>
        ))}
      </div>
      <div className="flex items-center gap-1.5 rounded-md border border-pb-border bg-pb-card-elevated/40 px-2.5 py-1.5">
        <span className="text-dense-2 uppercase tracking-wider text-pb-text-muted">
          Effectiveness
        </span>
        {EFFECTIVENESS.map((effectiveness) => (
          <ContainmentChip
            key={effectiveness}
            className={EFFECT_TONE[effectiveness]}
          >
            {EFFECTIVENESS_SHORT[effectiveness]}{" "}
            {byEffectiveness[effectiveness]}
          </ContainmentChip>
        ))}
      </div>
      <Metric label="Overdue" value={overdueCount} danger={overdueCount > 0} />
      <Metric label="Unowned" value={unowned} danger={unowned > 0} />
    </div>
  );
}

type SortKey = "priority" | "eta" | "status" | "effectiveness" | "owner";
type GroupKey = "none" | "status" | "priority" | "effectiveness";

export function ContainmentPlanEditor({
  data: incomingData,
  onChange,
  readOnly,
  onCreateJira,
  noteAuthor,
}: WidgetBodyProps<ContainmentPlanParams> & {
  onCreateJira?: ((action: ContainmentPlanAction) => void) | undefined;
  noteAuthor?: string | undefined;
}): ReactElement {
  const [data, setData] = useState(incomingData);
  const [previousIncomingData, setPreviousIncomingData] =
    useState(incomingData);
  const [pendingData, setPendingData] = useState<ContainmentPlanParams | null>(
    null,
  );
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [sortKey, setSortKey] = useState<SortKey>("priority");
  const [statusFilter, setStatusFilter] = useState<
    "all" | ContainmentActionStatus
  >("all");
  const [effectFilter, setEffectFilter] = useState<
    "all" | ContainmentEffectiveness
  >("all");
  const [groupKey, setGroupKey] = useState<GroupKey>("none");
  if (previousIncomingData !== incomingData) {
    const incomingJson = JSON.stringify(incomingData);
    const previousIncomingJson = JSON.stringify(previousIncomingData);
    const pendingJson = pendingData ? JSON.stringify(pendingData) : null;
    setPreviousIncomingData(incomingData);
    if (
      !pendingData ||
      incomingJson === pendingJson ||
      incomingJson !== previousIncomingJson
    ) {
      setPendingData(null);
      setData(incomingData);
    }
  }
  const commit = (
    update: (current: ContainmentPlanParams) => ContainmentPlanParams,
    options?: WidgetChangeOptions,
  ): void | Promise<void> => {
    const next = update(data);
    setPendingData(next);
    setData(next);
    return options ? onChange(next, options) : onChange(next);
  };
  const patch = (
    id: string,
    next: Partial<ContainmentPlanAction>,
    options?: WidgetChangeOptions,
  ): void | Promise<void> =>
    commit(
      (current) => ({
        ...current,
        actions: current.actions.map((action) =>
          action.id === id ? { ...action, ...next } : action,
        ),
      }),
      options,
    );
  const visible = useMemo(() => {
    return data.actions
      .filter(
        (action) => statusFilter === "all" || action.status === statusFilter,
      )
      .filter(
        (action) =>
          effectFilter === "all" || action.effectiveness === effectFilter,
      )
      .sort((a, b) => {
        if (sortKey === "priority")
          return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
        if (sortKey === "eta") return localDateMs(a.eta) - localDateMs(b.eta);
        if (sortKey === "status")
          return STATUS_RANK[a.status] - STATUS_RANK[b.status];
        if (sortKey === "effectiveness")
          return (
            EFFECTIVENESS_RANK[a.effectiveness] -
            EFFECTIVENESS_RANK[b.effectiveness]
          );
        return String(a[sortKey]).localeCompare(String(b[sortKey]));
      });
  }, [data.actions, effectFilter, sortKey, statusFilter]);
  const groups = useMemo(() => {
    if (groupKey === "none") return [["", visible]] as const;
    const map = new Map<string, ContainmentPlanAction[]>();
    visible.forEach((action) => {
      const key = action[groupKey];
      map.set(key, [...(map.get(key) ?? []), action]);
    });
    return [...map.entries()];
  }, [groupKey, visible]);
  const addAction = (): void => {
    const id = nextEditorId("containment");
    commit((current) => ({
      ...current,
      actions: [
        ...current.actions,
        {
          id,
          action: "New containment action",
          owner: "",
          eta: toLocalIsoDate(new Date()),
          priority: "Medium",
          status: "Open",
          effectiveness: "Not yet assessed",
          notes: [],
        },
      ],
    }));
    setOpenRows((current) => ({ ...current, [id]: true }));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      <RollupStrip actions={data.actions} />
      <div className="flex flex-wrap items-center gap-2">
        <Select
          ariaLabel="Sort containment actions"
          value={sortKey}
          onChange={(value) => setSortKey(value as SortKey)}
          options={["priority", "eta", "status", "effectiveness", "owner"]}
          labels={{
            priority: "Sort: Priority",
            eta: "Sort: ETA",
            status: "Sort: Status",
            effectiveness: "Sort: Effectiveness",
            owner: "Sort: Owner",
          }}
        />
        <Select
          ariaLabel="Filter containment status"
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as typeof statusFilter)}
          options={["all", ...STATUSES]}
          labels={{ all: "Status: All" }}
        />
        <Select
          ariaLabel="Filter containment effectiveness"
          value={effectFilter}
          onChange={(value) => setEffectFilter(value as typeof effectFilter)}
          options={["all", ...EFFECTIVENESS]}
          labels={{ all: "Effectiveness: All" }}
        />
        <Select
          ariaLabel="Group containment actions"
          value={groupKey}
          onChange={(value) => setGroupKey(value as GroupKey)}
          options={["none", "status", "priority", "effectiveness"]}
          labels={{
            none: "Group: None",
            status: "Group: Status",
            priority: "Group: Priority",
            effectiveness: "Group: Effectiveness",
          }}
        />
        <label className="ms-auto text-dense-1 text-pb-text-muted">
          Revisit every{" "}
          <input
            aria-label="Revisit cadence days"
            disabled={readOnly}
            type="number"
            min={1}
            value={data.revisitCadenceDays ?? ""}
            onChange={(e) =>
              commit((current) => ({
                ...current,
                revisitCadenceDays: e.target.value
                  ? Math.max(1, Number(e.target.value))
                  : undefined,
              }))
            }
            className="w-14 rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-xs text-pb-text"
          />{" "}
          days · next{" "}
          <input
            aria-label="Next review date"
            disabled={readOnly}
            type="date"
            value={data.nextReviewDate ?? ""}
            onChange={(e) =>
              commit((current) => ({
                ...current,
                nextReviewDate: e.target.value || undefined,
              }))
            }
            className="rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-xs text-pb-text"
          />
        </label>
      </div>
      {!readOnly ? (
        <button
          type="button"
          onClick={addAction}
          className="inline-flex w-fit items-center gap-1.5 rounded-md border border-pb-cyan/30 bg-pb-cyan/10 px-2.5 py-1.5 text-xs font-medium text-pb-cyan-text"
        >
          <Plus className="h-3.5 w-3.5" />
          Add action
        </button>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pe-1">
        {data.actions.length === 0 ? (
          <div className="rounded-md border border-dashed border-pb-border px-3 py-6 text-center text-xs italic text-pb-text-muted">
            No containment actions — add one for each escape point in the flow.
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-md border border-dashed border-pb-border px-3 py-6 text-center text-xs italic text-pb-text-muted">
            No actions match the current filters.
          </div>
        ) : (
          groups.map(([group, actions]) => (
            <div key={group || "all"} className="flex flex-col gap-2">
              {group ? (
                <div className="text-dense-1 font-semibold uppercase tracking-wider text-pb-text-secondary">
                  {group} · {actions.length}
                </div>
              ) : null}
              {actions.map((action) => (
                <ActionEditor
                  key={action.id}
                  action={action}
                  open={openRows[action.id] ?? false}
                  readOnly={readOnly}
                  onCreateJira={onCreateJira}
                  noteAuthor={noteAuthor}
                  onToggle={() =>
                    setOpenRows((current) => ({
                      ...current,
                      [action.id]: !current[action.id],
                    }))
                  }
                  onPatch={(next, options) => patch(action.id, next, options)}
                  onDelete={() =>
                    commit((current) => ({
                      ...current,
                      actions: current.actions.filter(
                        (candidate) => candidate.id !== action.id,
                      ),
                    }))
                  }
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  danger,
}: {
  label: string;
  value: number;
  danger?: boolean;
}): ReactElement {
  return (
    <div className="flex flex-col justify-center rounded-md border border-pb-border bg-pb-card-elevated/40 px-2.5 py-1">
      <div className="text-dense-2 uppercase tracking-wider text-pb-text-muted">
        {label}
      </div>
      <div
        className={cn(
          "text-lg font-semibold leading-tight",
          danger ? "text-pb-red-text" : "text-pb-text",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function Select({
  ariaLabel,
  value,
  onChange,
  options,
  labels,
  disabled,
}: {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  labels?: Readonly<Record<string, string>> | undefined;
  disabled?: boolean | undefined;
}): ReactElement {
  return (
    <select
      aria-label={ariaLabel}
      disabled={disabled}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-8 rounded-md border border-pb-control-border bg-pb-card px-2 text-xs text-pb-text"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {labels?.[option] ?? option}
        </option>
      ))}
    </select>
  );
}

function ActionEditor({
  action,
  open,
  readOnly,
  onToggle,
  onPatch,
  onDelete,
  onCreateJira,
  noteAuthor,
}: {
  action: ContainmentPlanAction;
  open: boolean;
  readOnly?: boolean | undefined;
  onToggle: () => void;
  /** With `grantViewerIfMissing`, a promise that settles with the save. */
  onPatch: (
    next: Partial<ContainmentPlanAction>,
    options?: WidgetChangeOptions,
  ) => void | Promise<void>;
  onDelete: () => void;
  onCreateJira?: ((action: ContainmentPlanAction) => void) | undefined;
  noteAuthor?: string | undefined;
}): ReactElement {
  const [note, setNote] = useState("");
  const people = usePeopleSource();
  const postNote = (): void => {
    const text = note.trim();
    if (!text) return;
    onPatch({
      notes: [
        {
          id: nextEditorId("note"),
          at: Date.now(),
          text,
          ...(noteAuthor?.trim() ? { author: noteAuthor.trim() } : {}),
        },
        ...action.notes,
      ],
    });
    setNote("");
  };
  return (
    <div className="rounded-md border border-pb-border bg-pb-card-elevated/30">
      <div className="flex flex-col gap-2 p-2.5">
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={onToggle}
            aria-label={
              open
                ? `Collapse notes for ${action.action}`
                : `Expand notes for ${action.action}`
            }
            className="rounded p-0.5 text-pb-text-muted"
          >
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          <input
            aria-label={`Containment action ${action.action}`}
            disabled={readOnly}
            value={action.action}
            onChange={(e) => onPatch({ action: e.target.value })}
            className="min-w-0 flex-1 rounded-md border-b border-transparent bg-transparent text-xs font-medium text-pb-text focus-field"
          />
          <ContainmentChip className={EFFECT_TONE[action.effectiveness]}>
            {action.effectiveness}
          </ContainmentChip>
          {!readOnly ? (
            <DisabledReason
              {...(action.jiraKey || onCreateJira
                ? {}
                : { reason: "Jira integration is not connected" })}
            >
              <button
                type="button"
                aria-label={
                  action.jiraKey
                    ? `Remove Jira link ${action.jiraKey}`
                    : onCreateJira
                      ? `Push ${action.action} to Jira`
                      : `Jira integration unavailable for ${action.action}`
                }
                disabled={!action.jiraKey && !onCreateJira}
                onClick={() => {
                  if (action.jiraKey) {
                    onPatch({ jiraKey: undefined });
                  } else {
                    onCreateJira?.(action);
                  }
                }}
                className="inline-flex items-center gap-1 rounded border border-pb-control-border px-1.5 py-0.5 text-dense-2 text-pb-text-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Ticket className="h-3 w-3" />
                {action.jiraKey ?? "Jira"}
              </button>
            </DisabledReason>
          ) : null}
          {!readOnly ? (
            <button
              type="button"
              aria-label={`Delete containment action ${action.action}`}
              onClick={onDelete}
              className="rounded p-1 text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-2 ps-6 md:grid-cols-5">
          <Field label="Priority">
            <Select
              ariaLabel={`${action.action} priority`}
              disabled={readOnly}
              value={action.priority}
              onChange={(value) =>
                onPatch({ priority: value as ContainmentPriority })
              }
              options={PRIORITIES}
            />
          </Field>
          <Field label="Status">
            <Select
              ariaLabel={`${action.action} status`}
              disabled={readOnly}
              value={action.status}
              onChange={(value) =>
                onPatch({ status: value as ContainmentActionStatus })
              }
              options={STATUSES}
            />
          </Field>
          <Field label="Effectiveness">
            <Select
              ariaLabel={`${action.action} effectiveness`}
              disabled={readOnly}
              value={action.effectiveness}
              onChange={(value) =>
                onPatch({ effectiveness: value as ContainmentEffectiveness })
              }
              options={EFFECTIVENESS}
            />
          </Field>
          {people ? (
            <div className="min-w-0">
              <WidgetPersonField
                source={people}
                label="Owner"
                allowTyped={false}
                text={action.owner}
                userId={action.ownerUserId}
                readOnly={readOnly}
                onChange={(next, options) =>
                  onPatch(
                    { owner: next.text, ownerUserId: next.userId },
                    options,
                  )
                }
              />
            </div>
          ) : (
            <Field label="Owner">
              <input
                aria-label={`${action.action} owner`}
                disabled={readOnly}
                readOnly
                value={action.owner}
                className="h-8 w-full rounded-md border border-pb-control-border bg-pb-card px-2 text-xs text-pb-text"
              />
              <span className="text-dense-1 text-pb-text-muted">
                People search is unavailable here.
              </span>
            </Field>
          )}
          <Field label="ETA">
            <input
              aria-label={`${action.action} ETA`}
              disabled={readOnly}
              type="date"
              value={action.eta}
              onChange={(e) => {
                if (e.target.value) onPatch({ eta: e.target.value });
              }}
              className="h-8 w-full rounded-md border border-pb-control-border bg-pb-card px-2 text-xs text-pb-text"
            />
          </Field>
        </div>
      </div>
      {open ? (
        <div className="border-t border-pb-border bg-pb-card/40 px-9 py-2">
          <ContainmentActionDetailsFields
            action={action}
            readOnly={readOnly}
            onUpdate={onPatch}
          />
          <div className="text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
            Notes ({action.notes.length})
          </div>
          {action.notes.map((item) => (
            <div
              key={item.id}
              className="group mt-2 flex items-start gap-2 rounded-md border border-pb-border bg-pb-card px-2.5 py-1.5 text-xs"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {item.author ? (
                    <strong className="text-pb-text">{item.author}</strong>
                  ) : null}
                  <span className="text-dense-2 text-pb-text-muted">
                    {noteTime(item.at)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap break-words text-pb-text-secondary">
                  {item.text}
                </p>
              </div>
              {!readOnly ? (
                <button
                  type="button"
                  aria-label={`Delete note ${item.text}`}
                  onClick={() =>
                    onPatch({
                      notes: action.notes.filter(
                        (candidate) => candidate.id !== item.id,
                      ),
                    })
                  }
                  className="rounded text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          ))}
          {!readOnly ? (
            <div className="mt-2 flex items-start gap-2">
              <textarea
                aria-label={`Add note to ${action.action}`}
                placeholder="Add a progress note…"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    (event.metaKey || event.ctrlKey)
                  ) {
                    event.preventDefault();
                    postNote();
                  }
                }}
                className="min-w-0 flex-1 resize-y rounded-md border border-pb-control-border bg-pb-card px-2.5 py-1.5 text-xs text-pb-text"
              />
              <button
                type="button"
                aria-label={`Post note to ${action.action}`}
                disabled={!note.trim()}
                onClick={postNote}
                className="inline-flex items-center gap-1 rounded-md border border-pb-amber/30 bg-pb-amber/10 px-2.5 py-1.5 text-xs font-medium text-pb-amber-text disabled:opacity-40"
              >
                <Send className="h-3 w-3" /> Post
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <label className="min-w-0 text-dense-2 uppercase tracking-wider text-pb-text-muted">
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}
